/**
 * Role → landing URL mapping — shared across all 4 frontend apps (each
 * used to carry its own copy, explicitly marked "keep in sync until
 * graduated to packages/shared").
 *
 * Dev hostnames are bare `localhost:PORT` for all 4 apps — simplicity
 * over cross-app cookie isolation, a deliberate choice. Known tradeoff:
 * local-dev session cookies are host-only (no `Domain` attribute), and
 * browsers scope cookies by exact hostname, not by port, so all 4 apps
 * on bare `localhost` share one cookie jar — logging into technical
 * (:5182) as a TECHNICIAN and then visiting management (:5181) sends
 * that same session cookie, and management's AuthGuard
 * correctly-but-confusingly bounces the request back to :5182. (A prior
 * revision gave each app its own `*.localhost` subdomain — e.g.
 * `management.localhost:5181` — to isolate cookies and match prod's
 * real subdomain isolation; reverted for bare ports on request.) Use
 * separate browser profiles/incognito windows if you need two
 * different-role sessions open across apps at once.
 */

export type App = 'admin' | 'management' | 'technical' | 'web'

const PROD_BASE: Readonly<Record<App, string>> = {
  admin: 'https://admin.vollgaskfz.at',
  // `mgmt` / `tech`, not `management` / `technical`. The short forms are the
  // serving hosts; management.vollgaskfz.at is a Firebase redirect onto mgmt,
  // and technical.vollgaskfz.at was never registered. Landing straight on the
  // serving host avoids a redirect hop on every staff sign-in.
  management: 'https://mgmt.vollgaskfz.at',
  technical: 'https://tech.vollgaskfz.at',
  web: 'https://vollgaskfz.at',
}

const DEV_BASE: Readonly<Record<App, string>> = {
  admin: 'http://localhost:5183',
  management: 'http://localhost:5181',
  technical: 'http://localhost:5182',
  web: 'http://localhost:5180',
}

export interface LandingForOptions {
  /** Pass the CALLING app's own `import.meta.env.DEV` — Vite substitutes
   *  env vars per-app at build time, so this module can't read it itself. */
  readonly isDev: boolean
  /** The calling app's own `VITE_URL_*` overrides (prod, non-default deploys). */
  readonly envBase: Readonly<Partial<Record<App, string | undefined>>>
  /** Path under the web base where CUSTOMER (and unknown) roles land.
   *  Omit for the marketing home. Only apps/web knows whether its customer
   *  dashboard ships, so it passes `/dashboard` when that flag is on. */
  readonly customerPath?: string
}

/** Build a `landingFor(roleKey)` bound to the calling app's own env. */
export function createLandingFor({ isDev, envBase, customerPath = '' }: LandingForOptions) {
  const baseFor = (app: App): string => {
    if (isDev) return DEV_BASE[app]
    // `||`, not `??`. An unset GitHub Actions variable renders as an empty
    // string, and Vite inlines empty VITE_* values from process.env in
    // preference to the .env files — so a variable nobody set arrives here
    // as '' rather than undefined. `??` would keep it, and the callers pass
    // this straight to window.location.replace(), where '' reloads the
    // current page: an infinite redirect loop instead of a fallback.
    return envBase[app] || PROD_BASE[app]
  }
  return function landingFor(roleKey: string | null | undefined): string {
    switch (roleKey) {
      case 'ADMIN':
        return baseFor('admin')
      case 'MANAGER':
        return baseFor('management')
      case 'TECHNICIAN':
        return baseFor('technical')
      case 'CUSTOMER':
      default:
        // Marketing home unless the caller says its customer dashboard
        // ships — with the dashboard withheld, `/dashboard` 404s. This is a
        // live path, not a theoretical one: AuthController is shared, so a
        // CUSTOMER-role account can sign in on management/technical/admin,
        // and all three staff AuthGuards hard-redirect a disallowed role
        // here with window.location.replace. The `default` arm makes that
        // true of any unrecognised role too.
        return baseFor('web') + customerPath
    }
  }
}
